import { Suspense, useEffect } from 'react';
import { HashRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import WebApp from '@twa-dev/sdk';
import { useAppStore } from './store/useAppStore';
import { Navbar } from './components/Navbar';
import { CourseListPage } from './pages/CourseListPage';
import { CoursePickerPage } from './pages/CoursePickerPage';
import { ChannelGate } from './components/ChannelGate';
import { LessonViewPage } from './pages/LessonViewPage';
import { Toaster } from 'react-hot-toast';
import { FloatingReward } from './components/FloatingReward';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
      retry: 1,
    },
  },
});

import { ErrorBoundary } from './components/ErrorBoundary';
import { ConfirmProvider } from './components/ConfirmProvider';
import { LoginPage } from './pages/LoginPage';
import { lazyWithRetry } from './lib/lazyWithRetry';
import { PageFallback } from './components/PageFallback';

/*
 * O'quvchining asosiy yo'li (kurslar, dars, kirish) darhol yuklanadi;
 * qolgan sahifalar — kerak bo'lganda. Ustoz rejimi, imtihonlar, profil
 * (sertifikat + QR) o'quvchilarning ko'pchiligiga birinchi ochilishda
 * kerak emas, lekin ilgari hammasi bitta 1.37 MB lik faylda edi.
 */
const LeaderboardPage = lazyWithRetry(() =>
  import('./pages/LeaderboardPage').then((m) => m.LeaderboardPage),
);
const ProfilePage = lazyWithRetry(() =>
  import('./pages/ProfilePage').then((m) => m.ProfilePage),
);
const AiChatPage = lazyWithRetry(() =>
  import('./pages/AiChatPage').then((m) => m.AiChatPage),
);
const AiReviewPage = lazyWithRetry(() =>
  import('./pages/AiReviewPage').then((m) => m.AiReviewPage),
);
const TeacherViewPage = lazyWithRetry(() =>
  import('./pages/TeacherViewPage').then((m) => m.TeacherViewPage),
);
const ExamListPage = lazyWithRetry(() =>
  import('./pages/ExamListPage').then((m) => m.ExamListPage),
);
const ExamRunnerPage = lazyWithRetry(() =>
  import('./pages/ExamRunnerPage').then((m) => m.ExamRunnerPage),
);
const ExamResultPage = lazyWithRetry(() =>
  import('./pages/ExamResultPage').then((m) => m.ExamResultPage),
);

export const App = () => {
  const {
    role,
    initFromUrlOrTelegram,
    resolveIdentity,
    enrollments,
    courseId,
    tgInitData,
    webToken,
  } = useAppStore();

  /*
   * KIM EKANLIGI NOMAʼLUM — kirish sahifasi.
   *
   * Telegram ichida `initData` boʻladi; saytda esa saqlangan sessiya.
   * Ikkalasi ham yoʻq boʻlsa, ilova ilgari oʻzini butunlay oʻchirib
   * qoʻyardi ("Access Denied"). Endi kirish taklif qilinadi.
   *
   * Birinchi renderda `initFromUrlOrTelegram` hali ishlamagan boʻlishi
   * mumkin, lekin `webToken` doʻkonga localStorage'dan DARHOL
   * yuklanadi — yaʼni saytdagi oʻquvchi kirish sahifasini koʻrib
   * qolmaydi.
   */
  const needsLogin = !tgInitData && !webToken;

  /*
   * Tanlangan kurs majburiy kanal talab qiladi-yu, oʻquvchi aʼzo emas —
   * darslar oʻrniga kanal ekrani. Server ham kurs soʻrovlarini 403 bilan
   * toʻxtatadi; bu yerda faqat tushunarli yoʻl koʻrsatamiz.
   */
  const gated =
    role === 'student'
      ? enrollments.find(
          (e) => e.courseId === courseId && e.channel?.member === false,
        )
      : undefined;

  useEffect(() => {
    try {
      WebApp.ready();
      WebApp.expand();
      // Sarlavha/fon ranglarini `theme.ts` boshqaradi — bu yerda qattiq
      // yozilgan qora rang light mode'ni buzardi.
    } catch {
      // Running outside telegram environment
    }
    /*
     * Ikki bosqich, va tartib muhim.
     *
     * 1) URL'dan — SINXRON. Bu `tgInitData` ni ham qo'yadi, ya'ni
     *    keyingi so'rov imzolangan bo'ladi. Ilova darhol biror narsa
     *    ko'rsata boshlaydi.
     * 2) Serverdan — ASINXRON, va u yutadi. URL menyu tugmasiga bir
     *    marta yozilgan eski qiymat bo'lishi mumkin; initData esa doim
     *    haqiqatni aytadi.
     */
    initFromUrlOrTelegram();
    void resolveIdentity();
  }, [initFromUrlOrTelegram, resolveIdentity]);

  if (needsLogin) {
    return (
      <QueryClientProvider client={queryClient}>
        <Toaster position="top-center" />
        <LoginPage />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ConfirmProvider>
        <HashRouter>
          <div style={{ minHeight: '100vh', position: 'relative' }}>
            <Toaster
              position="top-center"
              toastOptions={{
                style: {
                  background: 'var(--surface)',
                  color: 'var(--ink)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)',
                },
              }}
            />
            <ErrorBoundary>
              {gated ? (
                <ChannelGate enrollment={gated} />
              ) : (
                <Suspense fallback={<PageFallback />}>
                  <Routes>
                    <Route
                      path="/"
                      element={
                        role === 'teacher' ? (
                          <TeacherViewPage />
                        ) : /*
                        Kurs tanlanmagan-u, tanlash uchun bir nechta
                        variant bor -> tanlagich. Bitta kursda u
                        avtomatik tanlanadi (`resolveIdentity`), ya'ni bu
                        shart bajarilmaydi va o'quvchi avvalgidek
                        to'g'ridan-to'g'ri darslarni ko'radi.
                      */
                        !courseId && enrollments.length > 1 ? (
                          <CoursePickerPage />
                        ) : (
                          <CourseListPage />
                        )
                      }
                    />
                    <Route
                      path="/lesson/:lessonId"
                      element={<LessonViewPage />}
                    />
                    <Route path="/leaderboard" element={<LeaderboardPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/chat" element={<AiChatPage />} />
                    <Route
                      path="/ai-review/:moduleId"
                      element={<AiReviewPage />}
                    />
                    <Route path="/exams" element={<ExamListPage />} />
                    <Route path="/exam/:examId" element={<ExamRunnerPage />} />
                    <Route
                      path="/exam/:examId/result"
                      element={<ExamResultPage />}
                    />
                  </Routes>
                </Suspense>
              )}
            </ErrorBoundary>
            <Navbar />
            <FloatingReward />
          </div>
        </HashRouter>
      </ConfirmProvider>
    </QueryClientProvider>
  );
};
export default App;
